import React, { useState, useEffect, useContext, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import GridLayout from "react-grid-layout";
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';
import { AiOutlineDrag } from "react-icons/ai";
import './EsgPerformanceTab.scss';
import { RegulationContext, LoaderContext } from '../../../App';
import DashGraphConfigService from '../../../core/service/DashGraphConfigService';
import Utils from '../../../core/utilities/Utils';
import CompanyGraphCard from '../CompanyDashboardGraph/CompanyGraphCard';
import ModernChartCard from './ModernChartCard';
import { LS_AUTHORITY_KEY, GraphLayoutType, USER_ROLE } from '../../../core/constants/common';
import useHasPermission from '../../../core/hooks/useHasPermission';
import Nodataimg from "../../../images/Assessment.png";

const EsgPerformanceTab = ({ 
  data, 
  selectedCategory, 
  selectedYear, 
  refreshTrigger, 
  onMaximizeChart, 
  onTableViewModal,
  getChartShowLabels,
  onChartShowLabelsChange,
  // New props for embedded mode
  scopeGraphs,
  layoutConfig: propLayoutConfig,
  scope,
  layout: propLayout,
  isEmbedded = false
}) => {
  const { t } = useTranslation();
  const { glbRegulation, setGlbRegulation } = useContext(RegulationContext);
  const glbRegulationId = glbRegulation?.id ? glbRegulation.id : "";
  const { setLoaderDisplay } = useContext(LoaderContext);
  
  const authority = JSON.parse(localStorage.getItem(LS_AUTHORITY_KEY));
  const companyId = authority?.profile?.company?.id;
  const standardId = glbRegulation?.id;
    
  const [containerWidth, setContainerWidth] = useState(window.innerWidth);
  const [dynamicWidgets, setDynamicWidgets] = useState([]);
  const [layout, setLayout] = useState([]);
  const [renderedOn, setRenderedOn] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [layoutConfig, setLayoutConfig] = useState({});
  const [filter, setFilter] = useState({
    year: selectedYear,
    type: selectedCategory || 'Environmental',
  });
  const fetchRequestIdRef = useRef(0);

  // Sync selectedYear prop with filter.year state when prop changes
  useEffect(() => {
    if (selectedYear !== undefined && selectedYear !== filter.year) {
      setFilter(prev => ({ ...prev, year: selectedYear }));
    }
  }, [selectedYear]);

  useEffect(() => {
    if (selectedCategory && selectedCategory !== filter.type) {
      setFilter(prev => ({ ...prev, type: selectedCategory }));
    }
  }, [selectedCategory]);


  // Calculate grid layout width based on container width
  const gridLayoutWidth = containerWidth <= 780 ? containerWidth - 70 : containerWidth - 280;
  const { hasDashboardViewPermission } = useHasPermission();

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      setContainerWidth(window.innerWidth);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const categoryToApiFormat = (categoryName) => {
    if (categoryName === 'Social') return 'SOCIAL';
    if (categoryName === 'Governance') return 'GOVERNANCE';
    if (categoryName === 'ALL') return null;
    return 'ENVIRONMENTAL';
  };

  // Fetch dynamic widgets
  const fetchDynamicWidgets = async () => {
    if (!companyId || !standardId) return;

    const requestId = ++fetchRequestIdRef.current;
    const categoryAtFetch = selectedCategory;

    setLoading(true);
    setError(null);
    
    try {
      // In embedded mode, use the provided scopeGraphs instead of making API call
      if (isEmbedded) {
        if (scopeGraphs && scopeGraphs.length > 0) {
          // Process the layout data correctly
          const processedLayout = Utils.setupDynamicGraphLayoutConfigFor({ 
            graphs: scopeGraphs, 
            lData: propLayoutConfig?.layout, // Use the layout property from propLayoutConfig
            activeLayoutConfig: layoutConfig
          });

          if (requestId !== fetchRequestIdRef.current) return;

          setDynamicWidgets(scopeGraphs);
          setLayoutConfig({ 
            id: propLayoutConfig?.id, 
            layout: processedLayout
          });
          setLayout(processedLayout);
          setRenderedOn(Date.now());
          setLoading(false);
          return;
        } else {
          // No data in embedded mode
          if (requestId !== fetchRequestIdRef.current) return;
          setDynamicWidgets([]);
          setLayoutConfig({});
          setLayout([]);
          setRenderedOn(Date.now());
          setLoading(false);
          return;
        }
      }

      const category = categoryToApiFormat(categoryAtFetch);

      // For embedded mode with scope/layout parameters, use them in the API call
      let response;
      if (isEmbedded && scope && propLayout) {
        // Parse layout type from scope for the API call
        const graphLayoutType = parseLayoutTypeFromFilter(scope);
        response = await DashGraphConfigService.getCompanyGraphs(
          companyId, 
          standardId, 
          category, 
          glbRegulation?.name, 
          null, 
          null, 
          true, 
          false, 
          graphLayoutType?.text, 
          '', 
          graphLayoutType?.value
        );
      } else {
        // Default API call for standalone mode
        response = await DashGraphConfigService.getCompanyGraphs(
          companyId, 
          standardId, 
          category, 
          glbRegulation?.name, 
          null, 
          false, 
          true // active widgets only
        );
      }

      if (response.status === 200) {
        if (requestId !== fetchRequestIdRef.current) return;

        const graphs = response.data?.graphs || [];
        const processedLayout = Utils.setupDynamicGraphLayoutConfigFor({ 
          graphs: graphs, 
          lData: response.data?.layout,
          activeLayoutConfig: layoutConfig
        });
        
        setDynamicWidgets(graphs);
        setLayoutConfig({ 
          id: response.data?.layout?.id, 
          layout: processedLayout
        });
        
        // Use the processed layout directly from the API
        setLayout(processedLayout);
        
        setRenderedOn(Date.now());
      }
    } catch (err) {
      if (requestId !== fetchRequestIdRef.current) return;
      console.error('Error fetching dynamic widgets:', err);
      setError(t("errorLoadingWidgets.label"));
    } finally {
      if (requestId === fetchRequestIdRef.current) {
        setLoading(false);
      }
    }
  };

  // Fetch widgets on mount and when dependencies change
  useEffect(() => {
    if (glbRegulationId && selectedCategory) {
      fetchDynamicWidgets();
    }
  }, [glbRegulationId, selectedCategory, refreshTrigger, isEmbedded, scopeGraphs, scope, propLayout]);

  const onLayoutChange = (newLayout) => {
    setLayout(newLayout);
  };

  const handleDragStop = (layout, oldItem, newItem) => {
    setLayout(layout);
    saveLayout(layout);
  };

  const parseLayoutTypeFromFilter = (f) => {
    console.log('EsgPerformanceTab parseLayoutTypeFromFilter called with:', f);
    switch (f) {
      case 'All':
      case GraphLayoutType.OVERVIEW.name: 
        console.log('EsgPerformanceTab matched OVERVIEW case');
        return GraphLayoutType.OVERVIEW;
      case GraphLayoutType.SCOPE_1.name: 
        console.log('EsgPerformanceTab matched SCOPE_1 case');
        return GraphLayoutType.SCOPE_1;
      case GraphLayoutType.SCOPE_2.name: 
        console.log('EsgPerformanceTab matched SCOPE_2 case');
        return GraphLayoutType.SCOPE_2;
      case GraphLayoutType.SCOPE_3.name: 
        console.log('EsgPerformanceTab matched SCOPE_3 case');
        return GraphLayoutType.SCOPE_3;
      default: 
        console.log('EsgPerformanceTab matched default case');
        return { name: '', value: '' };
    }
  };

  const saveLayout = (updatedLayout) => {
    const layout = [];
    updatedLayout?.forEach(l => {
      // Handle both graph-{id} and dynamic-widget-{id} formats
      let widgetId;
      if (l.i.startsWith('graph-')) {
        widgetId = +(l.i.split('-')[1]);
      } else if (l.i.startsWith('dynamic-widget-')) {
        widgetId = +(l.i.split('-')[2]); // dynamic-widget-123 -> 123
      } else {
        // For static widgets, skip them as they don't need to be saved
        return;
      }
      
      layout.push({
        i: widgetId,
        x: l.x,
        y: l.y,
        w: l.w,
        h: l.h,
        minW: l.minW,
        minH: l.minH
      });
    });
    // Add other category layouts
    layoutConfig?.layout?.forEach(l => {
      const id = +(l.i.split('-')[1]);
      if (layout.findIndex(o => o.i === id) < 0) {
        layout.push({
          i: +(l.i.split('-')[1]),
          x: l.x,
          y: l.y,
          w: l.w,
          h: l.h,
          minW: l.minW,
          minH: l.minH
        });
      }
    });
    // For embedded mode, include the GraphLayoutType in the layout config
    let lConfig;
    if (isEmbedded && scope) {
      const graphLayoutType = parseLayoutTypeFromFilter(scope);
      // Special case for Overview: use "SCOPE_OVERVIEW" instead of "OVERVIEW"
      const layoutType = graphLayoutType.name === 'OVERVIEW' ? 'SCOPE_OVERVIEW' : graphLayoutType.name;
      console.log('EsgPerformanceTab saving layout with type:', layoutType, 'for scope:', scope);
      lConfig = {
        ...layoutConfig,
        layout: layout,
        type: layoutType
      };
    } else {
      lConfig = {
        ...layoutConfig,
        layout: layout
      };
    }
    
    setLoaderDisplay(true);
    DashGraphConfigService.saveGraphLayout(glbRegulationId, lConfig).then(res => {
      setLoaderDisplay(false);
      if (res.status !== 200 && res.status !== 201) return;

    }).catch(err => {
      setLoaderDisplay(false);
    });
  };

  const handleResizeStop = (layout, oldItem, newItem) => {
    saveLayout(layout);
  };

  const handleDragStart = (layout, oldItem, newItem) => {
    console.log('Drag started:', oldItem);
  };

  const handleDrag = (layout, oldItem, newItem) => {
    setLayout(layout);
  };

  const handleDrop = (layout, item, e) => {
    console.log('Drop event:', item);
    setLayout(layout);
  };

  
  const getFilteredLayoutForESGGraphs = () => {
    const graphIds = [];
    dynamicWidgets?.forEach(g => graphIds.push(g.id));
    
    // Use the current layout state which should contain the saved layout
    const filteredLayout = layout.filter(l => {
      const widgetId = +(l.i.split('-')[1]);
      return graphIds.indexOf(widgetId) >= 0;
    });
    
    return filteredLayout;
  };

  const handleRemoveWidget = async (widgetId) => {
    // Check user permissions similar to Dashboard.js
    const lsData = JSON.parse(localStorage.getItem(LS_AUTHORITY_KEY));
    let isCompanyAdmin = false;
    
    // if (PermissionUtils.hasCompanyAdminPermission(lsData?.roles)) {
    if (hasDashboardViewPermission()) {
      isCompanyAdmin = true;
    } else {
      isCompanyAdmin = false;
    }

    // Only allow deactivation if user has company admin permissions
    if (!isCompanyAdmin) {
      console.warn('User does not have permission to deactivate widgets');
      // You might want to show a toast notification here
      return;
    }

    try {
      // Show loading state
      setLoaderDisplay(true);
      
      // Call API to deactivate the widget using activateGraph with status false
      await DashGraphConfigService.activateGraph(widgetId, false);
      
      // Remove from local state
      setDynamicWidgets(prev => prev.filter(widget => widget.id !== widgetId));
      
      // Update layout to remove the widget
      setLayout(prev => prev.filter(item => item.i !== `graph-${widgetId}`));
      
      // Show success message (you might want to add a toast notification here)
      console.log('Widget deactivated successfully');
      
    } catch (error) {
      console.error('Error removing widget:', error);
      // You might want to show a toast notification here for error
    } finally {
      // Hide loading state
      setLoaderDisplay(false);
    }
  };

  const renderDynamicWidget = (widget) => (
    <ModernChartCard
      key={`graph-${widget.id}`}
      config={widget}
      year={selectedYear}
      renderedOn={renderedOn}
      title={widget.title}
      type={widget.type}
      data={widget.data}
      onMaximize={() => onMaximizeChart(widget)}
      onTableViewModal={onTableViewModal}
      onDeactivate={() => handleRemoveWidget(widget.id)}
      showLabels={getChartShowLabels ? getChartShowLabels(widget.id) : undefined}
      onShowLabelsChange={
        onChartShowLabelsChange
          ? (show) => onChartShowLabelsChange(widget.id, show)
          : undefined
      }
    />
  );

  if (loading) {
    return (
      <div className="esg-performance-tab">
        <div className="esg-performance-tab__loading">
          <div className="loading-spinner"></div>
          <p>{t("loadingWidgets.label")}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="esg-performance-tab">
        <div className="esg-performance-tab__error">
          <p>{error}</p>
          <button onClick={fetchDynamicWidgets}>{t("retry.label")}</button>
        </div>
      </div>
    );
  }

  // Show no data message when there are no widgets
  if (!loading && dynamicWidgets.length === 0) {
    return (
      <div className="esg-performance-tab">
        <div className="col-lg-12 px-0 py-2 text-center">
          <div className="noDataCard">
            <div>
              <div className="Nodataimg_box">
                <img src={Nodataimg} alt="No data" />
              </div>
              <h5 className="py-3">
                {t("noDataAvailable.label")}
              </h5>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="esg-performance-tab">
      <GridLayout
        className="layout"
        layout={getFilteredLayoutForESGGraphs()}
        cols={20}
        rowHeight={15}
        width={gridLayoutWidth}
        onLayoutChange={onLayoutChange}
        onResizeStop={handleResizeStop}
        onDragStop={handleDragStop}
        isResizable={true}
        isDraggable={true}
        draggableHandle=".drag-handle"
        margin={[20, 20]}
        containerPadding={[5, 10]}
      >
        {/* Dynamic Widgets */}
        {dynamicWidgets.map((widget) => (
          <div 
            key={`graph-${widget.id}`} 
            className="dashboard-widget"
          >
            {renderDynamicWidget(widget)}
          </div>
        ))}
      </GridLayout>
    </div>
  );
};

export default EsgPerformanceTab; 